import { useMemo, useState } from 'react';
import { DataTable } from '@ui';
import { useInfiniteScroll } from '@heroui/use-infinite-scroll';
import { SortDescriptor, Spinner } from '@ds';
import { useInfiniteList } from '@/hooks/useInfiniteList';
import { renderDeviceCell } from './renderDeviceCell';
import { OcsConsoleData, TrialProgramsFilterType } from '@/api/apis/resources';
import { defaultDeviceSort, getOCSColumns } from './config';
import { CaseResourceCombined, OrganType, ResourceLogisticsStatus } from '@/api/apis/case';
import { Uuid } from '@/api/types';
import { useInfiniteOcsConsoleListQuery } from '@/queries/ocs-consoles';
import { TrialProgram } from '@/api/apis/facilities/trial-programs';
import { getDevicesListWithAssigned, getOCSDisabledKeys, mapSelected } from '../utils';

type ResourceTableProps = {
  disableReserve?: boolean;
  caseId: Uuid;
  isLogistics: boolean;
  hubId?: Uuid;
  searchTerm: string;
  organType: OrganType;
  assignedDevices?: CaseResourceCombined[];
  onDeviceAssign: (device: OcsConsoleData) => void;
  selectedDeviceId: string;
  isSwap?: boolean;
  trialProgram?: TrialProgram;
};

export const DeviceTable = ({
  disableReserve,
  caseId,
  isLogistics,
  searchTerm,
  hubId,
  organType,
  onDeviceAssign,
  assignedDevices = [],
  selectedDeviceId,
  isSwap,
  trialProgram,
}: ResourceTableProps) => {
  const [sort, setSort] = useState<SortDescriptor>(defaultDeviceSort);
  const { list, hasNextPage, fetchNextPage, isLoading } = useInfiniteList({
    query: useInfiniteOcsConsoleListQuery,
    queryParams: {
      isInMaintenance: false,
      nopHub: hubId,
      organType,
      orderBy: sort.column.toString(),
      asc: sort.direction === 'ascending',
      trialProgramIds: trialProgram?.id ?? undefined,
      trialProgramsFilterType: trialProgram ? TrialProgramsFilterType.AnyOf : TrialProgramsFilterType.None,
    },
    searchTerm,
    enabled: !!hubId,
  });
  const [loaderRef, scrollerRef] = useInfiniteScroll({
    hasMore: hasNextPage,
    onLoadMore: fetchNextPage,
  });

  const tableRows = useMemo(() => {
    const deviceIds = assignedDevices
      ?.filter((r) => r.logisticsStatus !== ResourceLogisticsStatus.OCSDone)
      .map((r) => r.resourceId);
    const listWithAssigned = getDevicesListWithAssigned(list, assignedDevices);
    const searchResults = mapSelected(
      [...deviceIds, ...(selectedDeviceId ? [selectedDeviceId] : [])],
      listWithAssigned,
      'id',
    );

    if (isSwap) return searchResults.filter((device) => !assignedDevices.find((d) => d.resourceId === device.id));
    return searchResults;
  }, [list, selectedDeviceId, assignedDevices, isSwap]);

  const renderCell = useMemo(() => {
    return renderDeviceCell(onDeviceAssign, isLogistics, caseId, isSwap, disableReserve);
  }, [onDeviceAssign, isLogistics, caseId, disableReserve, isSwap]);

  const disabledKeys = useMemo(() => {
    return getOCSDisabledKeys(tableRows);
  }, [tableRows]);

  return (
    <DataTable
      baseRef={scrollerRef}
      type="infinite"
      label="Devices"
      baseClassName="min-h-[400px] max-h-[400px] overflow-auto"
      hidePadding
      columns={getOCSColumns(isSwap)}
      renderCell={renderCell}
      data={tableRows}
      bottomContent={
        hasNextPage ? (
          <div className="flex w-full justify-center">
            <Spinner size="md" ref={loaderRef} />
          </div>
        ) : null
      }
      disabledKeys={isSwap ? disabledKeys : []}
      isLoading={false}
      isFetching={isLoading}
      sortBy={sort}
      onSortByChange={setSort}
      isHeaderSticky
    />
  );
};
